"use client";

import { useQuery } from "@tanstack/react-query";
import { forwardRef } from "react";

import { Segment } from "#app/(unauthorized)/authentication/segment";
import { useJwtSegment } from "#app/(unauthorized)/authentication/useJwt";
import { allQueryKeys } from "#app/_api/allQueryKeys";
import { TimeSpan } from "#app/lib/TimeSpan";
import { Icon } from "#ui/icon";
import {
  LinkButtonWithIndicator,
  type LinkButtonWithIndicatorProps,
  type LinkButtonWithIndicatorRef,
} from "#ui/link";
import type { NavRouteSub } from "#ui/nav.utils";

type LinkDocumentsReceivedRef = LinkButtonWithIndicatorRef;

interface LinkDocumentsReceivedProps extends Omit<
  LinkButtonWithIndicatorProps,
  "children" | "count" | "href" | "iconOnly" | "size" | "variant"
> {
  route: NavRouteSub;
}

const LinkDocumentsReceived = forwardRef<
  LinkDocumentsReceivedRef,
  LinkDocumentsReceivedProps
>(({ className, route, ...props }, ref) => {
  const segment = useJwtSegment();
  const { data: unreadDocumentCount } = useQuery({
    ...allQueryKeys.externalSecureDocumentService.documentsReceivedCount,
    staleTime: TimeSpan.fromMinutes(1).totalMilliseconds,
    enabled: segment !== Segment.NotLoggedIn,
  });
  return (
    <LinkButtonWithIndicator
      {...props}
      href={route.path}
      count={unreadDocumentCount || 0}
      variant="ghost"
      iconOnly
      ref={ref}
    >
      <Icon name="ms-notifications-filled" size="lg" />
      <span className="sr-only">{route.element}:</span>
    </LinkButtonWithIndicator>
  );
});

LinkDocumentsReceived.displayName = "LinkDocumentsReceived";

export { LinkDocumentsReceived };
